/**
 * SystemFlowDiagram - Event Model visualization with Screens, Slices, Commands, Events, and Read Models
 *
 * Key Event Modeling concepts:
 * - SCREENS: UI screens at the top (trigger commands, display read models)
 * - SLICES: Vertical columns representing complete feature flows (the key unit!)
 * - Commands: User intentions (blue)
 * - Events: Facts that happened (orange)
 * - Read Models: Data views (green)
 * - Swimlanes: Domain boundaries (horizontal rows)
 */

import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Text, tokens } from '../design-system';

// Layout constants - much wider slices to prevent crowding
const LAYOUT = {
  screenRowHeight: 120,    // Row for screens at top
  sliceHeaderHeight: 50,   // Header for slice name
  commandRowHeight: 70,    // Row for commands
  swimlaneHeight: 90,      // Each domain swimlane
  readModelRowHeight: 80,  // Row for read models
  sliceWidth: 240,         // Much wider slices!
  sliceGap: 20,            // Gap between slices
  boxWidth: 160,           // Wider boxes
  boxHeight: 44,
  screenBoxWidth: 180,     // Screen boxes are wider
  screenBoxHeight: 100,    // Screen boxes show components
  leftMargin: 160,         // Space for swimlane labels
  topPadding: 10,
};

// Colors - Warm palette aligned with tokens
const COLORS = {
  screen: { bg: '#DCE8FB', border: '#5B8FD9', text: '#3A65B5' },  // Warm blue for screens
  command: { bg: '#5B8FD9', border: '#4A7AC7', text: '#FAF9F7' },
  event: { bg: '#F97316', border: '#EA580C', text: '#FAF9F7' },
  readModel: { bg: '#78B7B4', border: '#5FA09C', text: '#FAF9F7' },
  automation: { bg: '#FF8C1A', border: '#EA580C', text: '#FAF9F7' },
  sliceHeader: '#FFF2E0',
  sliceHeaderText: '#4A3520',
  sliceBorder: '#D4B896',
  swimlaneBg: ['#FFF8EE', '#FFF2E0'],
  swimlaneBorder: '#E8E4DD',
  arrow: '#8B7355',
  labelText: '#4A3520',
  mutedText: '#A39B8B',
  screenArrow: '#5B8FD9',  // Warm blue arrows from screens to commands
  readModelArrow: '#78B7B4', // Verdigris arrows from read models to screens
};

function SystemFlowDiagram({ task, onMetadataChange }) {
  const [selectedSlice, setSelectedSlice] = useState(null);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [zoom, setZoom] = useState(1);
  // Focus state for highlighting related elements
  // { type: 'screen'|'command'|'event'|'readModel'|'slice', id: string }
  const [focusedElement, setFocusedElement] = useState(null);
  // Validation overlay toggle
  const [showValidation, setShowValidation] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  // Currently editing element: { type: 'command'|'event'|'readModel', sliceIdx: number, value: string, eventIdx?: number }
  const [editingElement, setEditingElement] = useState(null);
  // Context menu state: { x: number, y: number, type: string, sliceIdx: number, eventIdx?: number }
  const [contextMenu, setContextMenu] = useState(null);
  // New element modal state
  const [newElementModal, setNewElementModal] = useState(null);
  // Ref for input element
  const editInputRef = useRef(null);

  const metadata = task?.metadata || {};
  const chapters = metadata.chapters || [];
  const swimlanes = metadata.swimlanes || [];
  const screensData = metadata.screens || {};

  // Build diagram data with explicit slice structure
  const diagramData = useMemo(() => {
    const allSlices = [];
    chapters.forEach((chapter) => {
      (chapter.slices || []).forEach((slice, idx) => {
        allSlices.push({
          ...slice,
          chapter: chapter.name,
          chapterDescription: chapter.description,
          sliceName: slice.command || slice.read_model || `Slice ${idx + 1}`,
          sliceIndex: allSlices.length,
        });
      });
    });

    // Get swimlanes with their events
    const lanes = swimlanes.length > 0
      ? swimlanes.map(s => ({
          name: s.name,
          description: s.description || '',
          events: s.events || []
        }))
      : [{ name: 'System', description: '', events: [] }];

    // Get screens data
    const screens = screensData.screens || [];
    const commandTriggers = screensData.command_triggers || [];
    const readModelDisplays = screensData.read_model_displays || [];
    const hasScreens = screens.length > 0;

    const totalSliceWidth = LAYOUT.sliceWidth + LAYOUT.sliceGap;
    const numColumns = Math.max(allSlices.length, screens.length);
    const width = LAYOUT.leftMargin + (numColumns * totalSliceWidth) + 60;

    // Add screen row height if we have screens
    const screenRowHeight = hasScreens ? LAYOUT.screenRowHeight : 0;
    const screenGap = hasScreens ? 20 : 0; // Extra space between screens and commands
    // New layout order: Screens -> Commands -> Swimlanes -> Read Models -> Slices (at bottom)
    const height = LAYOUT.topPadding + screenRowHeight + screenGap + LAYOUT.commandRowHeight +
                   (lanes.length * LAYOUT.swimlaneHeight) + LAYOUT.readModelRowHeight + LAYOUT.sliceHeaderHeight + 40;

    // Build a map of event positions for drawing connections
    // eventPositions[eventName] = [{sliceIdx, eventIdx}]
    const eventPositions = {};
    allSlices.forEach((slice, sliceIdx) => {
      (slice.events || []).forEach((event, eventIdx) => {
        if (!eventPositions[event]) {
          eventPositions[event] = [];
        }
        eventPositions[event].push({ sliceIdx, eventIdx });
      });
    });

    // Build connections from source_events to read_models
    const sourceConnections = [];
    allSlices.forEach((slice, sliceIdx) => {
      if (slice.read_model && slice.source_events?.length > 0) {
        slice.source_events.forEach(sourceEvent => {
          const eventLocs = eventPositions[sourceEvent] || [];
          eventLocs.forEach(loc => {
            sourceConnections.push({
              fromSliceIdx: loc.sliceIdx,
              fromEventIdx: loc.eventIdx,
              toSliceIdx: sliceIdx,
              eventName: sourceEvent,
              readModel: slice.read_model,
            });
          });
        });
      }
    });

    // Build screen-to-command connections
    const screenCommandConnections = [];
    commandTriggers.forEach(trigger => {
      // Find which screen this trigger belongs to
      const screenIdx = screens.findIndex(s => s.id === trigger.screen_id);
      if (screenIdx === -1) return;

      // Find which slice has this command
      const sliceIdx = allSlices.findIndex(s => s.command === trigger.target);
      if (sliceIdx === -1) return;

      screenCommandConnections.push({
        screenIdx,
        sliceIdx,
        command: trigger.target,
        componentLabel: trigger.component_label,
      });
    });

    // Build read-model-to-screen connections
    const readModelScreenConnections = [];
    readModelDisplays.forEach(display => {
      // Find which screen this display belongs to
      const screenIdx = screens.findIndex(s => s.id === display.screen_id);
      if (screenIdx === -1) return;

      // Find which slice has this read model
      const sliceIdx = allSlices.findIndex(s => s.read_model === display.target);
      if (sliceIdx === -1) return;

      readModelScreenConnections.push({
        screenIdx,
        sliceIdx,
        readModel: display.target,
        componentLabel: display.component_label,
      });
    });

    // Calculate screen positions based on the leftmost slice they interact with
    // Each screen should be positioned above the first component it interacts with
    const rawScreenPositions = screens.map((screen, screenIdx) => {
      // Find all slices this screen interacts with (commands it triggers + read models it displays)
      const interactingSliceIndices = [];

      // Check commands this screen triggers
      screenCommandConnections.forEach(conn => {
        if (conn.screenIdx === screenIdx) {
          interactingSliceIndices.push(conn.sliceIdx);
        }
      });

      // Check read models this screen displays
      readModelScreenConnections.forEach(conn => {
        if (conn.screenIdx === screenIdx) {
          interactingSliceIndices.push(conn.sliceIdx);
        }
      });

      // Get the leftmost (minimum) slice index
      // If no connections found, use -1 to mark as "unpositioned"
      const leftmostSliceIdx = interactingSliceIndices.length > 0
        ? Math.min(...interactingSliceIndices)
        : -1;

      return {
        screenIdx,
        screenId: screen.id,
        positionSliceIdx: leftmostSliceIdx,
        hasConnections: interactingSliceIndices.length > 0,
      };
    });

    // Handle screens with no connections - place them at unused positions
    // Also handle overlapping screens - offset them
    const usedPositions = new Map(); // positionSliceIdx -> count of screens at that position
    const screenPositions = rawScreenPositions.map((sp, idx) => {
      let finalPosition = sp.positionSliceIdx;

      // If no connections, find an available position
      if (finalPosition === -1) {
        // Try to place after all used positions, or use screen index
        const maxUsed = Math.max(...Array.from(usedPositions.keys()), -1);
        finalPosition = Math.max(maxUsed + 1, idx);
      }

      // Check for overlap and offset if needed
      const existingCount = usedPositions.get(finalPosition) || 0;
      usedPositions.set(finalPosition, existingCount + 1);

      return {
        ...sp,
        positionSliceIdx: finalPosition,
        // Add horizontal offset within the slice if there are multiple screens at same position
        offsetInSlice: existingCount,
      };
    });

    return {
      slices: allSlices,
      swimlanes: lanes,
      width,
      height,
      totalSliceWidth,
      eventPositions,
      sourceConnections,
      screens,
      hasScreens,
      screenCommandConnections,
      readModelScreenConnections,
      commandTriggers,
      readModelDisplays,
      screenPositions,
    };
  }, [chapters, swimlanes, screensData]);

  const {
    slices, swimlanes: lanes, width, height, totalSliceWidth, sourceConnections,
    screens, hasScreens, screenCommandConnections, readModelScreenConnections,
    commandTriggers, readModelDisplays, screenPositions
  } = diagramData;

  // Compute which elements should be highlighted based on focused element
  const highlightedElements = useMemo(() => {
    if (!focusedElement) return null; // No focus = show everything

    const highlighted = {
      screens: new Set(),
      commands: new Set(),
      events: new Set(),
      readModels: new Set(),
      slices: new Set(),
    };

    const { type, id } = focusedElement;

    if (type === 'screen') {
      highlighted.screens.add(id);
      // Find commands this screen triggers
      const screenObj = screens.find(s => s.id === id);
      if (screenObj) {
        (screenObj.components || []).forEach(comp => {
          if (comp.triggers_command) {
            highlighted.commands.add(comp.triggers_command);
            // Find slice with this command and add its events
            const slice = slices.find(s => s.command === comp.triggers_command);
            if (slice) {
              highlighted.slices.add(slice.sliceIndex);
              (slice.events || []).forEach(e => highlighted.events.add(e));
            }
          }
          if (comp.displays_read_model) {
            highlighted.readModels.add(comp.displays_read_model);
          }
        });
      }
    } else if (type === 'command') {
      highlighted.commands.add(id);
      // Find slice with this command
      const slice = slices.find(s => s.command === id);
      if (slice) {
        highlighted.slices.add(slice.sliceIndex);
        (slice.events || []).forEach(e => highlighted.events.add(e));
        if (slice.read_model) highlighted.readModels.add(slice.read_model);
      }
      // Find screens that trigger this command
      screens.forEach(screen => {
        (screen.components || []).forEach(comp => {
          if (comp.triggers_command === id) {
            highlighted.screens.add(screen.id);
          }
        });
      });
    } else if (type === 'event') {
      highlighted.events.add(id);
      // Find slices that produce this event
      slices.forEach(slice => {
        if ((slice.events || []).includes(id)) {
          highlighted.slices.add(slice.sliceIndex);
          if (slice.command) highlighted.commands.add(slice.command);
        }
        // Find read models that source from this event
        if ((slice.source_events || []).includes(id)) {
          if (slice.read_model) highlighted.readModels.add(slice.read_model);
        }
      });
    } else if (type === 'readModel') {
      highlighted.readModels.add(id);
      // Find slice with this read model and its source events
      const slice = slices.find(s => s.read_model === id);
      if (slice) {
        highlighted.slices.add(slice.sliceIndex);
        (slice.source_events || []).forEach(e => highlighted.events.add(e));
      }
      // Find screens that display this read model
      screens.forEach(screen => {
        (screen.components || []).forEach(comp => {
          if (comp.displays_read_model === id) {
            highlighted.screens.add(screen.id);
          }
        });
      });
    } else if (type === 'slice') {
      const sliceIdx = parseInt(id);
      highlighted.slices.add(sliceIdx);
      const slice = slices[sliceIdx];
      if (slice) {
        if (slice.command) highlighted.commands.add(slice.command);
        (slice.events || []).forEach(e => highlighted.events.add(e));
        if (slice.read_model) highlighted.readModels.add(slice.read_model);
      }
    }

    return highlighted;
  }, [focusedElement, screens, slices]);

  // Validation logic - detect issues in the event model
  const validationIssues = useMemo(() => {
    const issues = {
      orphanedEvents: [],      // Events not triggered by any command
      commandsWithoutEvents: [], // Commands that don't produce any events
      readModelsWithoutSources: [], // Read models without source events
      eventsNotConsumed: [],   // Events that don't feed into any read model
      total: 0,
    };

    // Collect all events produced by commands (from slices)
    const eventsFromCommands = new Set();
    const allEvents = new Set();
    const eventsConsumedByReadModels = new Set();
    const commandsSet = new Set();
    const readModelsSet = new Set();

    slices.forEach(slice => {
      if (slice.command) {
        commandsSet.add(slice.command);
        if (slice.events && slice.events.length > 0) {
          slice.events.forEach(e => {
            eventsFromCommands.add(e);
            allEvents.add(e);
          });
        } else {
          // Command without events
          issues.commandsWithoutEvents.push({
            command: slice.command,
            sliceIndex: slice.sliceIndex,
            chapter: slice.chapter,
          });
        }
      }

      // Collect events from all slices (including those from automations)
      (slice.events || []).forEach(e => allEvents.add(e));

      // Track read models and their source events
      if (slice.read_model) {
        readModelsSet.add(slice.read_model);
        if (slice.source_events && slice.source_events.length > 0) {
          slice.source_events.forEach(e => eventsConsumedByReadModels.add(e));
        } else if (slice.type !== 'state_view') {
          // Read model without sources (unless it's a pure state view)
          issues.readModelsWithoutSources.push({
            readModel: slice.read_model,
            sliceIndex: slice.sliceIndex,
            chapter: slice.chapter,
          });
        }
      }
    });

    // Find orphaned events (events in swimlanes but not produced by any command)
    lanes.forEach(lane => {
      (lane.events || []).forEach(event => {
        if (!eventsFromCommands.has(event)) {
          // This event exists in a swimlane but isn't produced by any command
          issues.orphanedEvents.push({
            event,
            swimlane: lane.name,
          });
        }
      });
    });

    // Find events that aren't consumed by any read model
    allEvents.forEach(event => {
      if (!eventsConsumedByReadModels.has(event)) {
        issues.eventsNotConsumed.push({
          event,
          // Find which slice produces this event
          producer: slices.find(s => (s.events || []).includes(event))?.command || 'Unknown',
        });
      }
    });

    issues.total = issues.orphanedEvents.length +
                   issues.commandsWithoutEvents.length +
                   issues.readModelsWithoutSources.length +
                   issues.eventsNotConsumed.length;

    return issues;
  }, [slices, lanes]);

  // Helper to check if an element has validation issues
  const hasValidationIssue = (type, id) => {
    if (!showValidation) return false;

    if (type === 'event') {
      return validationIssues.orphanedEvents.some(i => i.event === id) ||
             validationIssues.eventsNotConsumed.some(i => i.event === id);
    }
    if (type === 'command') {
      return validationIssues.commandsWithoutEvents.some(i => i.command === id);
    }
    if (type === 'readModel') {
      return validationIssues.readModelsWithoutSources.some(i => i.readModel === id);
    }
    return false;
  };

  // Helper to check if an element should be dimmed
  const isDimmed = (type, id) => {
    if (!highlightedElements) return false; // No focus = nothing dimmed
    const set = highlightedElements[type];
    return set && !set.has(id);
  };

  // ========== EDIT MODE FUNCTIONS ==========

  // Start editing an element
  const startEditing = useCallback((type, sliceIdx, value, eventIdx = null) => {
    if (!editMode) return;
    setEditingElement({ type, sliceIdx, value, eventIdx });
    // Focus the input after render
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, [editMode]);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setEditingElement(null);
  }, []);

  // Save edited element
  const saveEdit = useCallback((newValue) => {
    if (!editingElement || !newValue.trim()) {
      cancelEditing();
      return;
    }

    // Clone the metadata to modify
    const newMetadata = JSON.parse(JSON.stringify(metadata));
    const { type, sliceIdx, eventIdx } = editingElement;

    // Find the slice in chapters and update
    let currentSliceIdx = 0;
    for (const chapter of newMetadata.chapters || []) {
      for (const slice of chapter.slices || []) {
        if (currentSliceIdx === sliceIdx) {
          if (type === 'command') {
            const oldCommand = slice.command;
            slice.command = newValue.trim();
            // Update swimlane events that reference this command (if any)
            // Also update any screen component triggers
            if (newMetadata.screens?.command_triggers) {
              newMetadata.screens.command_triggers.forEach(trigger => {
                if (trigger.target === oldCommand) {
                  trigger.target = newValue.trim();
                }
              });
            }
          } else if (type === 'event' && eventIdx !== null) {
            const oldEvent = slice.events[eventIdx];
            slice.events[eventIdx] = newValue.trim();
            // Update source_events in other slices
            for (const ch of newMetadata.chapters || []) {
              for (const s of ch.slices || []) {
                if (s.source_events) {
                  const idx = s.source_events.indexOf(oldEvent);
                  if (idx !== -1) {
                    s.source_events[idx] = newValue.trim();
                  }
                }
              }
            }
            // Update swimlane events
            (newMetadata.swimlanes || []).forEach(lane => {
              const idx = (lane.events || []).indexOf(oldEvent);
              if (idx !== -1) {
                lane.events[idx] = newValue.trim();
              }
            });
          } else if (type === 'readModel') {
            const oldReadModel = slice.read_model;
            slice.read_model = newValue.trim();
            // Update screen component displays
            if (newMetadata.screens?.read_model_displays) {
              newMetadata.screens.read_model_displays.forEach(display => {
                if (display.target === oldReadModel) {
                  display.target = newValue.trim();
                }
              });
            }
          }
          break;
        }
        currentSliceIdx++;
      }
    }

    // Call the callback to persist changes
    if (onMetadataChange) {
      onMetadataChange(newMetadata);
    }

    cancelEditing();
  }, [editingElement, metadata, onMetadataChange, cancelEditing]);

  // Handle key press in edit input
  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      saveEdit(e.target.value);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  }, [saveEdit, cancelEditing]);

  // Delete an element
  const deleteElement = useCallback((type, sliceIdx, eventIdx = null) => {
    if (!window.confirm(`Delete this ${type}?`)) return;

    const newMetadata = JSON.parse(JSON.stringify(metadata));
    let currentSliceIdx = 0;

    for (const chapter of newMetadata.chapters || []) {
      for (let i = 0; i < (chapter.slices || []).length; i++) {
        const slice = chapter.slices[i];
        if (currentSliceIdx === sliceIdx) {
          if (type === 'command') {
            // Remove command from slice
            delete slice.command;
          } else if (type === 'event' && eventIdx !== null) {
            // Remove specific event
            slice.events.splice(eventIdx, 1);
          } else if (type === 'readModel') {
            delete slice.read_model;
            delete slice.source_events;
          }
          break;
        }
        currentSliceIdx++;
      }
    }

    if (onMetadataChange) {
      onMetadataChange(newMetadata);
    }
    setContextMenu(null);
  }, [metadata, onMetadataChange]);

  // Add a new element
  const addElement = useCallback((type, sliceIdx, value) => {
    if (!value.trim()) return;

    const newMetadata = JSON.parse(JSON.stringify(metadata));
    let currentSliceIdx = 0;

    for (const chapter of newMetadata.chapters || []) {
      for (const slice of chapter.slices || []) {
        if (currentSliceIdx === sliceIdx) {
          if (type === 'event') {
            if (!slice.events) slice.events = [];
            slice.events.push(value.trim());
          }
          break;
        }
        currentSliceIdx++;
      }
    }

    if (onMetadataChange) {
      onMetadataChange(newMetadata);
    }
    setNewElementModal(null);
  }, [metadata, onMetadataChange]);

  // Handle context menu
  const handleContextMenu = useCallback((e, type, sliceIdx, eventIdx = null) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      type,
      sliceIdx,
      eventIdx,
    });
  }, [editMode]);

  // Close context menu when clicking outside
  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Early return for empty state (after all hooks)
  if (chapters.length === 0) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>🔄</div>
        <Text style={styles.emptyTitle}>No System Flow Data</Text>
        <Text style={styles.emptyText}>
          This project doesn't have event model data yet.
        </Text>
      </div>
    );
  }

  // Calculate Y positions for each row
  // New order: Screens -> Commands -> Swimlanes -> Read Models -> Slices (at bottom)
  const screenRowY = LAYOUT.topPadding;
  const screenRowHeight = hasScreens ? LAYOUT.screenRowHeight : 0;
  const screenGap = hasScreens ? 20 : 0; // Extra space between screens and commands
  const commandRowY = LAYOUT.topPadding + screenRowHeight + screenGap;
  const firstSwimlaneY = commandRowY + LAYOUT.commandRowHeight;
  const readModelRowY = firstSwimlaneY + (lanes.length * LAYOUT.swimlaneHeight);
  const sliceHeaderY = readModelRowY + LAYOUT.readModelRowHeight;

  // Find which swimlane an event belongs to
  const getEventSwimlane = (eventName) => {
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i].events.includes(eventName)) {
        return i;
      }
    }
    return 0;
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <Text style={styles.title}>System Flow</Text>
          <Text style={styles.subtitle}>
            Each column is a Slice - click to see scenarios
          </Text>
        </div>
        <div style={styles.controls}>
          {/* Edit mode toggle */}
          <button
            style={{
              ...styles.editModeBtn,
              backgroundColor: editMode ? '#DCE8FB' : 'transparent',
              borderColor: editMode ? '#5B8FD9' : '#D4CFC5',
              color: editMode ? '#3A65B5' : '#7A6F5C',
            }}
            onClick={() => {
              setEditMode(!editMode);
              if (editMode) {
                // Exiting edit mode - clear edit state
                setEditingElement(null);
                setContextMenu(null);
                setNewElementModal(null);
              }
            }}
            title={editMode ? "Exit edit mode" : "Enter edit mode to modify elements"}
          >
            {editMode ? '✓ Editing' : '✏️ Edit'}
          </button>
          <div style={styles.controlDivider} />
          {/* Validation toggle */}
          <button
            style={{
              ...styles.validationBtn,
              backgroundColor: showValidation ? '#FAE0D8' : 'transparent',
              borderColor: showValidation ? '#D24E26' : '#D4CFC5',
              color: showValidation ? '#B83E1C' : '#7A6F5C',
            }}
            onClick={() => setShowValidation(!showValidation)}
            title="Toggle validation overlay"
          >
            ⚠️ Validate
            {validationIssues.total > 0 && (
              <span style={styles.issueBadge}>{validationIssues.total}</span>
            )}
          </button>
          <div style={styles.controlDivider} />
          <button style={styles.zoomBtn} onClick={() => setZoom(z => Math.min(1.5, z + 0.1))}>+</button>
          <span style={styles.zoomLevel}>{Math.round(zoom * 100)}%</span>
          <button style={styles.zoomBtn} onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}>−</button>
        </div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {hasScreens && (
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendBox, backgroundColor: COLORS.screen.bg, border: `2px solid ${COLORS.screen.border}` }} />
            <Text style={styles.legendText}>Screen</Text>
          </div>
        )}
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, backgroundColor: COLORS.command.bg }} />
          <Text style={styles.legendText}>Command</Text>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, backgroundColor: COLORS.event.bg }} />
          <Text style={styles.legendText}>Event</Text>
        </div>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendBox, backgroundColor: COLORS.readModel.bg }} />
          <Text style={styles.legendText}>Read Model</Text>
        </div>
        <Text style={styles.legendHint}>
          {hasScreens && `${screens.length} Screens • `}{slices.length} Slices • Time →
        </Text>
      </div>

      {/* Main content */}
      <div style={styles.mainContent}>
        {/* Diagram */}
        <div style={styles.diagramWrapper}>
          <svg
            width={width * zoom}
            height={height * zoom}
            viewBox={`0 0 ${width} ${height}`}
            style={{ minWidth: width * zoom }}
            onClick={() => setFocusedElement(null)}
          >
            {/* Background rect to capture clicks for clearing focus */}
            <rect x={0} y={0} width={width} height={height} fill="transparent" />
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill={COLORS.arrow} />
              </marker>
              <marker id="arrow-green" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill={COLORS.readModel.bg} />
              </marker>
              <marker id="arrow-blue" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill={COLORS.screenArrow} />
              </marker>
            </defs>

            {/* Screen row background and label */}
            {hasScreens && (
              <>
                <rect
                  x={0}
                  y={screenRowY}
                  width={width}
                  height={LAYOUT.screenRowHeight}
                  fill="#EEF4FD"
                  stroke={COLORS.swimlaneBorder}
                  strokeWidth={1}
                />
                <text x={10} y={screenRowY + LAYOUT.screenRowHeight / 2 + 5} fontSize="13" fontWeight="600" fill={COLORS.labelText}>
                  Screens
                </text>
              </>
            )}

            {/* Row labels on the left */}
            <text x={10} y={commandRowY + LAYOUT.commandRowHeight / 2 + 5} fontSize="13" fontWeight="600" fill={COLORS.labelText}>
              Commands
            </text>

            {/* Swimlane row labels */}
            {lanes.map((lane, laneIdx) => {
              const y = firstSwimlaneY + (laneIdx * LAYOUT.swimlaneHeight);
              return (
                <g key={lane.name}>
                  {/* Swimlane background */}
                  <rect
                    x={0}
                    y={y}
                    width={width}
                    height={LAYOUT.swimlaneHeight}
                    fill={COLORS.swimlaneBg[laneIdx % 2]}
                    stroke={COLORS.swimlaneBorder}
                    strokeWidth={1}
                  />
                  {/* Swimlane label */}
                  <text x={10} y={y + 24} fontSize="13" fontWeight="600" fill={COLORS.labelText}>
                    {truncate(lane.name, 16)}
                  </text>
                  <text x={10} y={y + 42} fontSize="10" fill={COLORS.mutedText}>
                    {truncate(lane.description, 20)}
                  </text>
                </g>
              );
            })}

            {/* Read Models row label */}
            <rect
              x={0}
              y={readModelRowY}
              width={width}
              height={LAYOUT.readModelRowHeight}
              fill="#F0F9F8"
              stroke={COLORS.swimlaneBorder}
              strokeWidth={1}
            />
            <text x={10} y={readModelRowY + LAYOUT.readModelRowHeight / 2 + 5} fontSize="13" fontWeight="600" fill={COLORS.labelText}>
              Read Models
            </text>

            {/* Slices row at bottom */}
            <rect
              x={0}
              y={sliceHeaderY}
              width={width}
              height={LAYOUT.sliceHeaderHeight}
              fill={COLORS.sliceHeader}
              stroke={COLORS.swimlaneBorder}
              strokeWidth={1}
            />
            <text x={10} y={sliceHeaderY + LAYOUT.sliceHeaderHeight / 2 + 5} fontSize="13" fontWeight="600" fill={COLORS.labelText}>
              Slices
            </text>

            {/* Render SCREENS at the top - positioned above their first interacting component */}
            {hasScreens && screens.map((screen, screenIdx) => {
              // Get the position for this screen (based on leftmost interacting slice)
              const screenPos = screenPositions?.find(sp => sp.screenIdx === screenIdx);
              const positionSliceIdx = screenPos?.positionSliceIdx ?? screenIdx;
              const offsetInSlice = screenPos?.offsetInSlice ?? 0;
              // If multiple screens at same position, offset them horizontally
              const overlapOffset = offsetInSlice * (LAYOUT.screenBoxWidth + 10);
              const screenX = LAYOUT.leftMargin + (positionSliceIdx * totalSliceWidth) + overlapOffset;
              const centerX = screenX + LAYOUT.sliceWidth / 2;
              const isSelected = selectedScreen === screenIdx;
              const dimmed = isDimmed('screens', screen.id);

              // Get components for this screen (show up to 4)
              const displayComponents = (screen.components || []).slice(0, 4);

              const handleScreenClick = (e) => {
                e.stopPropagation();
                setSelectedScreen(screenIdx);
                setFocusedElement(focusedElement?.id === screen.id ? null : { type: 'screen', id: screen.id });
              };

              return (
                <g key={`screen-${screenIdx}`} opacity={dimmed ? 0.2 : 1} style={{ transition: 'opacity 0.2s' }}>
                  {/* Screen box */}
                  <rect
                    x={centerX - LAYOUT.screenBoxWidth / 2}
                    y={screenRowY + 10}
                    width={LAYOUT.screenBoxWidth}
                    height={LAYOUT.screenBoxHeight}
                    rx={8}
                    fill={isSelected ? '#B4C8E8' : COLORS.screen.bg}
                    stroke={isSelected ? '#3A65B5' : COLORS.screen.border}
                    strokeWidth={isSelected ? 2.5 : 2}
                    style={{ cursor: 'pointer' }}
                    onClick={handleScreenClick}
                  />

                  {/* Screen name */}
                  <text
                    x={centerX}
                    y={screenRowY + 28}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="600"
                    fill={COLORS.screen.text}
                    style={{ cursor: 'pointer' }}
                    onClick={handleScreenClick}
                  >
                    {truncate(screen.name, 20)}
                  </text>

                  {/* Screen components preview */}
                  {displayComponents.map((comp, compIdx) => {
                    const compY = screenRowY + 42 + (compIdx * 16);
                    const isInteractive = comp.triggers_command;
                    const compColor = isInteractive ? '#5B8FD9' : '#78B7B4';

                    return (
                      <g key={compIdx}>
                        <rect
                          x={centerX - 70}
                          y={compY}
                          width={140}
                          height={14}
                          rx={3}
                          fill={isInteractive ? '#EEF4FD' : '#F0F9F8'}
                          stroke={compColor}
                          strokeWidth={1}
                        />
                        <text
                          x={centerX - 65}
                          y={compY + 10}
                          fontSize="9"
                          fill={compColor}
                        >
                          {comp.type === 'button' ? '▶' : comp.type === 'list' ? '≡' : '○'} {truncate(comp.label, 18)}
                        </text>
                      </g>
                    );
                  })}

                  {/* More components indicator */}
                  {(screen.components || []).length > 4 && (
                    <text
                      x={centerX}
                      y={screenRowY + LAYOUT.screenRowHeight - 8}
                      textAnchor="middle"
                      fontSize="9"
                      fill={COLORS.mutedText}
                    >
                      +{(screen.components || []).length - 4} more
                    </text>
                  )}
                </g>
              );
            })}

            {/* Screen to Command arrows */}
            {hasScreens && screenCommandConnections.map((conn, connIdx) => {
              // Look up actual screen position (may differ from screenIdx due to positioning)
              const screenPos = screenPositions?.find(sp => sp.screenIdx === conn.screenIdx);
              const positionSliceIdx = screenPos?.positionSliceIdx ?? conn.screenIdx;
              const offsetInSlice = screenPos?.offsetInSlice ?? 0;
              const overlapOffset = offsetInSlice * (LAYOUT.screenBoxWidth + 10);
              const fromScreenX = LAYOUT.leftMargin + (positionSliceIdx * totalSliceWidth) + overlapOffset + LAYOUT.sliceWidth / 2;
              const fromY = screenRowY + 10 + LAYOUT.screenBoxHeight;

              const toSliceX = LAYOUT.leftMargin + (conn.sliceIdx * totalSliceWidth) + LAYOUT.sliceWidth / 2;
              const toY = commandRowY + (LAYOUT.commandRowHeight - LAYOUT.boxHeight) / 2;

              // Draw curved line from screen to command
              const midY = (fromY + toY) / 2;

              return (
                <path
                  key={`screen-cmd-${connIdx}`}
                  d={`M ${fromScreenX} ${fromY}
                      Q ${fromScreenX} ${midY}, ${toSliceX} ${toY}`}
                  fill="none"
                  stroke={COLORS.screenArrow}
                  strokeWidth={1.5}
                  strokeDasharray="4,2"
                  opacity={0.7}
                  markerEnd="url(#arrow-blue)"
                />
              );
            })}

            {/* Read Model to Screen arrows - going upward from read models to screens at top */}
            {hasScreens && readModelScreenConnections.map((conn, connIdx) => {
              const fromSliceX = LAYOUT.leftMargin + (conn.sliceIdx * totalSliceWidth) + LAYOUT.sliceWidth / 2;
              const fromY = readModelRowY + 15; // top of read model box

              // Look up actual screen position (may differ from screenIdx due to positioning)
              const screenPos = screenPositions?.find(sp => sp.screenIdx === conn.screenIdx);
              const positionSliceIdx = screenPos?.positionSliceIdx ?? conn.screenIdx;
              const offsetInSlice = screenPos?.offsetInSlice ?? 0;
              const overlapOffset = offsetInSlice * (LAYOUT.screenBoxWidth + 10);
              const toScreenX = LAYOUT.leftMargin + (positionSliceIdx * totalSliceWidth) + overlapOffset + LAYOUT.sliceWidth / 2;
              const toY = screenRowY + 10 + LAYOUT.screenBoxHeight; // bottom of screen box

              // Draw curved line from read model up to screen (going upward)
              return (
                <path
                  key={`rm-screen-${connIdx}`}
                  d={`M ${fromSliceX} ${fromY}
                      C ${fromSliceX} ${fromY - 40}, ${toScreenX} ${toY + 40}, ${toScreenX} ${toY}`}
                  fill="none"
                  stroke={COLORS.readModelArrow}
                  strokeWidth={1.5}
                  strokeDasharray="4,2"
                  opacity={0.5}
                  markerEnd="url(#arrow-green)"
                />
              );
            })}

            {/* Render each SLICE as a distinct column */}
            {slices.map((slice, sliceIdx) => {
              const sliceX = LAYOUT.leftMargin + (sliceIdx * totalSliceWidth);
              const centerX = sliceX + LAYOUT.sliceWidth / 2;
              const isSelected = selectedSlice === sliceIdx;
              const sliceDimmed = isDimmed('slices', sliceIdx);

              const hasCommand = slice.command;
              const hasEvents = slice.events && slice.events.length > 0;
              const hasReadModel = slice.read_model;
              const isStateView = slice.type === 'state_view';

              const handleSliceClick = (e) => {
                e.stopPropagation();
                setSelectedSlice(sliceIdx);
                setFocusedElement(focusedElement?.type === 'slice' && focusedElement?.id === String(sliceIdx) ? null : { type: 'slice', id: String(sliceIdx) });
              };

              return (
                <g key={sliceIdx}>
                  {/* SLICE HEADER - This makes slices explicit! */}
                  <g opacity={sliceDimmed ? 0.2 : 1} style={{ transition: 'opacity 0.2s' }}>
                    <rect
                      x={sliceX}
                      y={sliceHeaderY}
                      width={LAYOUT.sliceWidth}
                      height={LAYOUT.sliceHeaderHeight}
                      fill={isSelected ? '#DCE8FB' : COLORS.sliceHeader}
                      stroke={isSelected ? COLORS.command.bg : COLORS.sliceBorder}
                      strokeWidth={isSelected ? 2 : 1}
                      rx={4}
                      style={{ cursor: 'pointer' }}
                      onClick={handleSliceClick}
                    />
                    <text
                      x={centerX}
                      y={sliceHeaderY + 24}
                      textAnchor="middle"
                      fontSize="12"
                      fontWeight="600"
                      fill={COLORS.sliceHeaderText}
                      style={{ cursor: 'pointer' }}
                      onClick={handleSliceClick}
                    >
                      {truncate(slice.sliceName, 22)}
                    </text>
                    <text
                      x={centerX}
                      y={sliceHeaderY + 42}
                      textAnchor="middle"
                      fontSize="10"
                      fill={COLORS.mutedText}
                      style={{ cursor: 'pointer' }}
                      onClick={handleSliceClick}
                    >
                      {slice.type === 'state_view' ? 'View' : 'State Change'}
                    </text>
                  </g>

                  {/* Slice vertical boundary lines - from commands row to slice headers at bottom */}
                  <line
                    x1={sliceX + LAYOUT.sliceWidth + LAYOUT.sliceGap / 2}
                    y1={commandRowY}
                    x2={sliceX + LAYOUT.sliceWidth + LAYOUT.sliceGap / 2}
                    y2={sliceHeaderY + LAYOUT.sliceHeaderHeight}
                    stroke={COLORS.sliceBorder}
                    strokeWidth={1}
                    strokeDasharray="4,4"
                    opacity={0.5}
                  />

                  {/* Command box (blue) */}
                  {hasCommand && (
                    <g
                      style={{ cursor: editMode ? 'text' : 'pointer', transition: 'opacity 0.2s' }}
                      opacity={isDimmed('commands', slice.command) ? 0.2 : 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSlice(sliceIdx);
                        setFocusedElement(focusedElement?.id === slice.command ? null : { type: 'command', id: slice.command });
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        if (editMode) {
                          startEditing('command', sliceIdx, slice.command);
                        }
                      }}
                      onContextMenu={(e) => handleContextMenu(e, 'command', sliceIdx)}
                    >
                      <rect
                        x={centerX - LAYOUT.boxWidth / 2}
                        y={commandRowY + (LAYOUT.commandRowHeight - LAYOUT.boxHeight) / 2}
                        width={LAYOUT.boxWidth}
                        height={LAYOUT.boxHeight}
                        rx={6}
                        fill={hasValidationIssue('command', slice.command) ? tokens.colors.error[100] : COLORS.command.bg}
                        stroke={editMode ? tokens.colors.info[500] : (hasValidationIssue('command', slice.command) ? tokens.colors.error[500] : (isSelected ? tokens.colors.info[700] : COLORS.command.border))}
                        strokeWidth={hasValidationIssue('command', slice.command) ? 3 : (isSelected ? 3 : (editMode ? 2 : 1.5))}
                        strokeDasharray={editMode ? '4,2' : '0'}
                      />
                      <text
                        x={centerX}
                        y={commandRowY + LAYOUT.commandRowHeight / 2 + 5}
                        textAnchor="middle"
                        fill={hasValidationIssue('command', slice.command) ? '#B83E1C' : COLORS.command.text}
                        fontSize="11"
                        fontWeight="500"
                      >
                        {truncate(slice.command, 22)}
                      </text>
                      {/* Edit mode indicator */}
                      {editMode && (
                        <text
                          x={centerX - LAYOUT.boxWidth / 2 + 8}
                          y={commandRowY + (LAYOUT.commandRowHeight - LAYOUT.boxHeight) / 2 + 12}
                          fontSize="10"
                          fill="white"
                        >✏️</text>
                      )}
                      {/* Validation warning badge */}
                      {hasValidationIssue('command', slice.command) && (
                        <g>
                          <circle
                            cx={centerX + LAYOUT.boxWidth / 2 - 8}
                            cy={commandRowY + (LAYOUT.commandRowHeight - LAYOUT.boxHeight) / 2 + 8}
                            r={10}
                            fill="#D24E26"
                          />
                          <text
                            x={centerX + LAYOUT.boxWidth / 2 - 8}
                            y={commandRowY + (LAYOUT.commandRowHeight - LAYOUT.boxHeight) / 2 + 12}
                            textAnchor="middle"
                            fontSize="11"
                            fill="white"
                            fontWeight="bold"
                          >!</text>
                        </g>
                      )}
                    </g>
                  )}

                  {/* Events in their swimlanes (orange) */}
                  {hasEvents && slice.events.map((event, eventIdx) => {
                    const laneIdx = getEventSwimlane(event);
                    const laneY = firstSwimlaneY + (laneIdx * LAYOUT.swimlaneHeight);
                    const eventDimmed = isDimmed('events', event);

                    // Stack multiple events vertically within the swimlane
                    const eventCount = slice.events.length;
                    const eventHeight = Math.min(LAYOUT.boxHeight, (LAYOUT.swimlaneHeight - 20) / eventCount - 4);
                    const eventY = laneY + 10 + (eventIdx * (eventHeight + 4));

                    return (
                      <g key={eventIdx}>
                        {/* Arrow from command to first event */}
                        {hasCommand && eventIdx === 0 && (
                          <line
                            x1={centerX}
                            y1={commandRowY + (LAYOUT.commandRowHeight + LAYOUT.boxHeight) / 2}
                            x2={centerX}
                            y2={eventY - 2}
                            stroke={COLORS.arrow}
                            strokeWidth={1.5}
                            markerEnd="url(#arrow)"
                          />
                        )}

                        {/* Event box */}
                        <g
                          style={{ cursor: editMode ? 'text' : 'pointer', transition: 'opacity 0.2s' }}
                          opacity={eventDimmed ? 0.2 : 1}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSlice(sliceIdx);
                            setFocusedElement(focusedElement?.id === event ? null : { type: 'event', id: event });
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            if (editMode) {
                              startEditing('event', sliceIdx, event, eventIdx);
                            }
                          }}
                          onContextMenu={(e) => handleContextMenu(e, 'event', sliceIdx, eventIdx)}
                        >
                          <rect
                            x={centerX - LAYOUT.boxWidth / 2}
                            y={eventY}
                            width={LAYOUT.boxWidth}
                            height={eventHeight}
                            rx={5}
                            fill={hasValidationIssue('event', event) ? '#FDF0D8' : COLORS.event.bg}
                            stroke={editMode ? '#EA580C' : (hasValidationIssue('event', event) ? '#D4881A' : (isSelected ? '#8B4E1A' : COLORS.event.border))}
                            strokeWidth={hasValidationIssue('event', event) ? 3 : (isSelected ? 2 : (editMode ? 2 : 1))}
                            strokeDasharray={editMode ? '4,2' : '0'}
                          />
                          <text
                            x={centerX}
                            y={eventY + eventHeight / 2 + 4}
                            textAnchor="middle"
                            fill={hasValidationIssue('event', event) ? '#7A4412' : COLORS.event.text}
                            fontSize="10"
                            fontWeight="500"
                          >
                            {truncate(event, 22)}
                          </text>
                          {/* Edit mode indicator */}
                          {editMode && (
                            <text
                              x={centerX - LAYOUT.boxWidth / 2 + 6}
                              y={eventY + 10}
                              fontSize="8"
                              fill="white"
                            >✏️</text>
                          )}
                          {/* Validation warning badge for events */}
                          {hasValidationIssue('event', event) && (
                            <g>
                              <circle
                                cx={centerX + LAYOUT.boxWidth / 2 - 6}
                                cy={eventY + 6}
                                r={8}
                                fill="#D4881A"
                              />
                              <text
                                x={centerX + LAYOUT.boxWidth / 2 - 6}
                                y={eventY + 10}
                                textAnchor="middle"
                                fontSize="10"
                                fill="white"
                                fontWeight="bold"
                              >!</text>
                            </g>
                          )}
                        </g>
                      </g>
                    );
                  })}

                  {/* Read Model box (green) */}
                  {hasReadModel && (
                    <g>
                      {/* Arrow from events to read model */}
                      {(hasEvents || isStateView) && (
                        <line
                          x1={centerX}
                          y1={readModelRowY - 8}
                          x2={centerX}
                          y2={readModelRowY + 12}
                          stroke={COLORS.arrow}
                          strokeWidth={1.5}
                          strokeDasharray={isStateView ? "5,3" : "0"}
                          markerEnd="url(#arrow)"
                        />
                      )}
                      <g
                        style={{ cursor: editMode ? 'text' : 'pointer', transition: 'opacity 0.2s' }}
                        opacity={isDimmed('readModels', slice.read_model) ? 0.2 : 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSlice(sliceIdx);
                          setFocusedElement(focusedElement?.id === slice.read_model ? null : { type: 'readModel', id: slice.read_model });
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (editMode) {
                            startEditing('readModel', sliceIdx, slice.read_model);
                          }
                        }}
                        onContextMenu={(e) => handleContextMenu(e, 'readModel', sliceIdx)}
                      >
                        <rect
                          x={centerX - LAYOUT.boxWidth / 2}
                          y={readModelRowY + 15}
                          width={LAYOUT.boxWidth}
                          height={LAYOUT.boxHeight}
                          rx={6}
                          fill={hasValidationIssue('readModel', slice.read_model) ? '#FDF0D8' : COLORS.readModel.bg}
                          stroke={editMode ? '#5FA09C' : (hasValidationIssue('readModel', slice.read_model) ? '#D4881A' : (isSelected ? '#3D7A76' : COLORS.readModel.border))}
                          strokeWidth={hasValidationIssue('readModel', slice.read_model) ? 3 : (isSelected ? 3 : (editMode ? 2 : 1.5))}
                          strokeDasharray={editMode ? '4,2' : '0'}
                        />
                        <text
                          x={centerX}
                          y={readModelRowY + 15 + LAYOUT.boxHeight / 2 + 4}
                          textAnchor="middle"
                          fill={hasValidationIssue('readModel', slice.read_model) ? '#7A4412' : COLORS.readModel.text}
                          fontSize="11"
                          fontWeight="500"
                        >
                          {truncate(slice.read_model, 22)}
                        </text>
                        {/* Edit mode indicator */}
                        {editMode && (
                          <text
                            x={centerX - LAYOUT.boxWidth / 2 + 8}
                            y={readModelRowY + 15 + 12}
                            fontSize="10"
                            fill="white"
                          >✏️</text>
                        )}
                        {/* Validation warning badge for read models */}
                        {hasValidationIssue('readModel', slice.read_model) && (
                          <g>
                            <circle
                              cx={centerX + LAYOUT.boxWidth / 2 - 8}
                              cy={readModelRowY + 15 + 8}
                              r={10}
                              fill="#D4881A"
                            />
                            <text
                              x={centerX + LAYOUT.boxWidth / 2 - 8}
                              y={readModelRowY + 15 + 12}
                              textAnchor="middle"
                              fontSize="11"
                              fill="white"
                              fontWeight="bold"
                            >!</text>
                          </g>
                        )}
                      </g>
                    </g>
                  )}
                </g>
              );
            })}

            {/* Source event connections to read models */}
            {sourceConnections.map((conn, connIdx) => {
              const fromSlice = slices[conn.fromSliceIdx];
              const toSlice = slices[conn.toSliceIdx];
              if (!fromSlice || !toSlice) return null;

              // Calculate event position
              const fromSliceX = LAYOUT.leftMargin + (conn.fromSliceIdx * totalSliceWidth);
              const fromCenterX = fromSliceX + LAYOUT.sliceWidth / 2;

              // Find which swimlane the event is in
              const eventLaneIdx = getEventSwimlane(conn.eventName);
              const eventLaneY = firstSwimlaneY + (eventLaneIdx * LAYOUT.swimlaneHeight);

              // Calculate event Y based on its index within the slice
              const eventCount = fromSlice.events?.length || 1;
              const eventHeight = Math.min(LAYOUT.boxHeight, (LAYOUT.swimlaneHeight - 20) / eventCount - 4);
              const fromEventY = eventLaneY + 10 + (conn.fromEventIdx * (eventHeight + 4)) + eventHeight;

              // Calculate read model position
              const toSliceX = LAYOUT.leftMargin + (conn.toSliceIdx * totalSliceWidth);
              const toCenterX = toSliceX + LAYOUT.sliceWidth / 2;
              const toY = readModelRowY + 15; // Top of read model box

              // Don't draw if same slice (already has vertical arrow)
              if (conn.fromSliceIdx === conn.toSliceIdx) return null;

              // Draw a curved path from event to read model
              const midY = (fromEventY + toY) / 2;
              const pathD = conn.fromSliceIdx < conn.toSliceIdx
                // Going right: curve down and right
                ? `M ${fromCenterX + LAYOUT.boxWidth/2} ${fromEventY - eventHeight/2}
                   Q ${fromCenterX + LAYOUT.boxWidth/2 + 30} ${midY},
                     ${toCenterX} ${toY - 5}`
                // Going left: curve down and left
                : `M ${fromCenterX - LAYOUT.boxWidth/2} ${fromEventY - eventHeight/2}
                   Q ${fromCenterX - LAYOUT.boxWidth/2 - 30} ${midY},
                     ${toCenterX} ${toY - 5}`;

              return (
                <path
                  key={connIdx}
                  d={pathD}
                  fill="none"
                  stroke={COLORS.readModel.bg}
                  strokeWidth={1.5}
                  strokeDasharray="5,3"
                  opacity={0.6}
                  markerEnd="url(#arrow-green)"
                />
              );
            })}
          </svg>
        </div>

        {/* Detail Panel */}
        {selectedSlice !== null && slices[selectedSlice] && (
          <div style={styles.detailPanel}>
            <div style={styles.detailHeader}>
              <div>
                <div style={styles.sliceBadge}>SLICE {selectedSlice + 1}</div>
                <Text style={styles.detailTitle}>
                  {slices[selectedSlice].sliceName}
                </Text>
                <Text style={styles.detailChapter}>
                  {slices[selectedSlice].chapter}
                </Text>
              </div>
              <button style={styles.closeBtn} onClick={() => setSelectedSlice(null)}>×</button>
            </div>

            {/* Slice type */}
            <div style={styles.typeBadge}>
              <span style={{
                ...styles.badge,
                backgroundColor: slices[selectedSlice].type === 'state_view' ? COLORS.readModel.bg : COLORS.command.bg
              }}>
                {slices[selectedSlice].type === 'state_view' ? 'State View' : 'State Change'}
              </span>
            </div>

            {/* Command */}
            {slices[selectedSlice].command && (
              <div style={styles.detailSection}>
                <Text style={styles.sectionTitle}>Command</Text>
                <span style={{ ...styles.tag, backgroundColor: COLORS.command.bg }}>
                  {slices[selectedSlice].command}
                </span>
              </div>
            )}

            {/* Events */}
            {slices[selectedSlice].events?.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.sectionTitle}>Events ({slices[selectedSlice].events.length})</Text>
                <div style={styles.tagList}>
                  {slices[selectedSlice].events.map((event, i) => (
                    <span key={i} style={{ ...styles.tag, backgroundColor: COLORS.event.bg }}>
                      {event}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Read Model */}
            {slices[selectedSlice].read_model && (
              <div style={styles.detailSection}>
                <Text style={styles.sectionTitle}>Read Model</Text>
                <span style={{ ...styles.tag, backgroundColor: COLORS.readModel.bg }}>
                  {slices[selectedSlice].read_model}
                </span>
              </div>
            )}

            {/* Source Events for Read Models */}
            {slices[selectedSlice].source_events?.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.sectionTitle}>Sourced From</Text>
                <div style={styles.tagList}>
                  {slices[selectedSlice].source_events.map((event, i) => (
                    <span key={i} style={{ ...styles.tag, backgroundColor: COLORS.event.bg }}>
                      {event}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* GWT Scenarios */}
            {slices[selectedSlice].gwt_scenarios?.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.sectionTitle}>
                  Scenarios ({slices[selectedSlice].gwt_scenarios.length})
                </Text>
                <div style={styles.gwtList}>
                  {slices[selectedSlice].gwt_scenarios.map((gwt, i) => (
                    <div key={i} style={styles.gwtCard}>
                      <div style={styles.gwtRow}>
                        <span style={styles.gwtLabel}>GIVEN</span>
                        <span style={styles.gwtText}>{gwt.given}</span>
                      </div>
                      {gwt.when && (
                        <div style={styles.gwtRow}>
                          <span style={styles.gwtLabel}>WHEN</span>
                          <span style={styles.gwtText}>{gwt.when}</span>
                        </div>
                      )}
                      <div style={styles.gwtRow}>
                        <span style={styles.gwtLabel}>THEN</span>
                        <span style={styles.gwtText}>{gwt.then}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Screen Detail Panel */}
        {selectedScreen !== null && screens[selectedScreen] && (
          <div style={styles.detailPanel}>
            <div style={styles.detailHeader}>
              <div>
                <div style={{ ...styles.sliceBadge, color: COLORS.screen.border }}>SCREEN</div>
                <Text style={styles.detailTitle}>
                  {screens[selectedScreen].name}
                </Text>
                {screens[selectedScreen].description && (
                  <Text style={styles.detailChapter}>
                    {screens[selectedScreen].description}
                  </Text>
                )}
              </div>
              <button style={styles.closeBtn} onClick={() => setSelectedScreen(null)}>×</button>
            </div>

            {/* Layout type */}
            <div style={styles.typeBadge}>
              <span style={{
                ...styles.badge,
                backgroundColor: COLORS.screen.border
              }}>
                {screens[selectedScreen].layout || 'single-column'}
              </span>
            </div>

            {/* Components that trigger commands */}
            {(() => {
              const triggerComps = (screens[selectedScreen].components || [])
                .filter(c => c.triggers_command);
              if (triggerComps.length === 0) return null;
              return (
                <div style={styles.detailSection}>
                  <Text style={styles.sectionTitle}>Triggers Commands ({triggerComps.length})</Text>
                  <div style={styles.gwtList}>
                    {triggerComps.map((comp, i) => (
                      <div key={i} style={styles.screenCompCard}>
                        <div style={styles.compRow}>
                          <span style={styles.compIcon}>
                            {comp.type === 'button' ? '▶' : comp.type === 'form' ? '📝' : '○'}
                          </span>
                          <span style={styles.compLabel}>{comp.label}</span>
                        </div>
                        <div style={styles.compTarget}>
                          → <span style={{ ...styles.tag, backgroundColor: COLORS.command.bg, padding: '2px 8px', fontSize: '11px' }}>
                            {comp.triggers_command}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Components that display read models */}
            {(() => {
              const displayComps = (screens[selectedScreen].components || [])
                .filter(c => c.displays_read_model);
              if (displayComps.length === 0) return null;
              return (
                <div style={styles.detailSection}>
                  <Text style={styles.sectionTitle}>Displays Read Models ({displayComps.length})</Text>
                  <div style={styles.gwtList}>
                    {displayComps.map((comp, i) => (
                      <div key={i} style={styles.screenCompCard}>
                        <div style={styles.compRow}>
                          <span style={styles.compIcon}>
                            {comp.type === 'list' ? '≡' : comp.type === 'table' ? '▦' : comp.type === 'chart' ? '📊' : '○'}
                          </span>
                          <span style={styles.compLabel}>{comp.label}</span>
                        </div>
                        <div style={styles.compTarget}>
                          ← <span style={{ ...styles.tag, backgroundColor: COLORS.readModel.bg, padding: '2px 8px', fontSize: '11px' }}>
                            {comp.displays_read_model}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Transitions */}
            {screens[selectedScreen].transitions?.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.sectionTitle}>Navigates To</Text>
                <div style={styles.tagList}>
                  {screens[selectedScreen].transitions.map((trans, i) => {
                    const targetScreen = screens.find(s => s.id === trans.to_screen);
                    return (
                      <div key={i} style={styles.transitionCard}>
                        <span style={styles.transitionTrigger}>On: {trans.trigger}</span>
                        <span style={styles.transitionArrow}>→</span>
                        <span style={styles.transitionTarget}>{targetScreen?.name || trans.to_screen}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Validation Summary Panel */}
        {showValidation && validationIssues.total > 0 && (
          <div style={styles.validationPanel}>
            <div style={styles.detailHeader}>
              <div>
                <div style={{ ...styles.sliceBadge, color: '#D24E26' }}>⚠️ VALIDATION</div>
                <Text style={styles.detailTitle}>
                  {validationIssues.total} Issue{validationIssues.total !== 1 ? 's' : ''} Found
                </Text>
              </div>
              <button style={styles.closeBtn} onClick={() => setShowValidation(false)}>×</button>
            </div>

            {/* Commands without events */}
            {validationIssues.commandsWithoutEvents.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.validationSectionTitle}>
                  🔴 Commands Without Events ({validationIssues.commandsWithoutEvents.length})
                </Text>
                <Text style={styles.validationHint}>
                  These commands don't produce any events
                </Text>
                <div style={styles.validationList}>
                  {validationIssues.commandsWithoutEvents.map((issue, i) => (
                    <div
                      key={i}
                      style={styles.validationItem}
                      onClick={() => {
                        setSelectedSlice(issue.sliceIndex);
                        setFocusedElement({ type: 'command', id: issue.command });
                      }}
                    >
                      <span style={{ ...styles.tag, backgroundColor: '#FAE0D8', color: '#B83E1C', fontSize: '11px' }}>
                        {issue.command}
                      </span>
                      <span style={styles.validationChapter}>{issue.chapter}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Orphaned events */}
            {validationIssues.orphanedEvents.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.validationSectionTitle}>
                  🟠 Orphaned Events ({validationIssues.orphanedEvents.length})
                </Text>
                <Text style={styles.validationHint}>
                  Events defined in swimlanes but not produced by any command
                </Text>
                <div style={styles.validationList}>
                  {validationIssues.orphanedEvents.map((issue, i) => (
                    <div
                      key={i}
                      style={styles.validationItem}
                      onClick={() => setFocusedElement({ type: 'event', id: issue.event })}
                    >
                      <span style={{ ...styles.tag, backgroundColor: '#FDF0D8', color: '#7A4412', fontSize: '11px' }}>
                        {issue.event}
                      </span>
                      <span style={styles.validationChapter}>in {issue.swimlane}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Events not consumed */}
            {validationIssues.eventsNotConsumed.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.validationSectionTitle}>
                  🟡 Events Not Consumed ({validationIssues.eventsNotConsumed.length})
                </Text>
                <Text style={styles.validationHint}>
                  Events that don't feed into any read model
                </Text>
                <div style={styles.validationList}>
                  {validationIssues.eventsNotConsumed.slice(0, 10).map((issue, i) => (
                    <div
                      key={i}
                      style={styles.validationItem}
                      onClick={() => setFocusedElement({ type: 'event', id: issue.event })}
                    >
                      <span style={{ ...styles.tag, backgroundColor: '#FDF0D8', color: '#7A4412', fontSize: '11px' }}>
                        {issue.event}
                      </span>
                      <span style={styles.validationChapter}>from {issue.producer}</span>
                    </div>
                  ))}
                  {validationIssues.eventsNotConsumed.length > 10 && (
                    <Text style={styles.validationHint}>
                      ... and {validationIssues.eventsNotConsumed.length - 10} more
                    </Text>
                  )}
                </div>
              </div>
            )}

            {/* Read models without sources */}
            {validationIssues.readModelsWithoutSources.length > 0 && (
              <div style={styles.detailSection}>
                <Text style={styles.validationSectionTitle}>
                  🟠 Read Models Without Sources ({validationIssues.readModelsWithoutSources.length})
                </Text>
                <Text style={styles.validationHint}>
                  Read models that don't have source events
                </Text>
                <div style={styles.validationList}>
                  {validationIssues.readModelsWithoutSources.map((issue, i) => (
                    <div
                      key={i}
                      style={styles.validationItem}
                      onClick={() => {
                        setSelectedSlice(issue.sliceIndex);
                        setFocusedElement({ type: 'readModel', id: issue.readModel });
                      }}
                    >
                      <span style={{ ...styles.tag, backgroundColor: '#FDF0D8', color: '#7A4412', fontSize: '11px' }}>
                        {issue.readModel}
                      </span>
                      <span style={styles.validationChapter}>{issue.chapter}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* No issues message */}
        {showValidation && validationIssues.total === 0 && (
          <div style={styles.validationPanel}>
            <div style={styles.noIssuesContainer}>
              <div style={styles.noIssuesIcon}>✅</div>
              <Text style={styles.noIssuesTitle}>No Issues Found</Text>
              <Text style={styles.noIssuesText}>
                Your event model looks good! All commands produce events,
                and all read models have sources.
              </Text>
            </div>
          </div>
        )}
      </div>

      {/* ========== EDIT MODE OVERLAYS ========== */}

      {/* Inline editing input overlay */}
      {editingElement && (
        <div
          style={styles.editOverlay}
          onClick={cancelEditing}
        >
          <div
            style={styles.editInputContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.editInputHeader}>
              <Text style={styles.editInputLabel}>
                Editing {editingElement.type === 'readModel' ? 'Read Model' : editingElement.type}
              </Text>
            </div>
            <input
              ref={editInputRef}
              type="text"
              defaultValue={editingElement.value}
              style={styles.editInput}
              onKeyDown={handleEditKeyDown}
              onBlur={(e) => saveEdit(e.target.value)}
              autoFocus
            />
            <div style={styles.editInputHint}>
              Press Enter to save, Esc to cancel
            </div>
          </div>
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          style={styles.contextMenuOverlay}
          onClick={closeContextMenu}
        >
          <div
            style={{
              ...styles.contextMenu,
              left: contextMenu.x,
              top: contextMenu.y,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              style={styles.contextMenuItem}
              onClick={() => {
                startEditing(
                  contextMenu.type,
                  contextMenu.sliceIdx,
                  slices[contextMenu.sliceIdx]?.[contextMenu.type === 'readModel' ? 'read_model' :
                    contextMenu.type === 'event' ? 'events' : 'command']?.[contextMenu.eventIdx] ||
                  slices[contextMenu.sliceIdx]?.[contextMenu.type === 'readModel' ? 'read_model' : 'command'],
                  contextMenu.eventIdx
                );
                closeContextMenu();
              }}
            >
              ✏️ Rename
            </button>
            {contextMenu.type === 'event' && (
              <button
                style={styles.contextMenuItem}
                onClick={() => {
                  setNewElementModal({ type: 'event', sliceIdx: contextMenu.sliceIdx });
                  closeContextMenu();
                }}
              >
                ➕ Add Event
              </button>
            )}
            <button
              style={{ ...styles.contextMenuItem, color: '#B83E1C' }}
              onClick={() => deleteElement(contextMenu.type, contextMenu.sliceIdx, contextMenu.eventIdx)}
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      )}

      {/* New element modal */}
      {newElementModal && (
        <div
          style={styles.editOverlay}
          onClick={() => setNewElementModal(null)}
        >
          <div
            style={styles.editInputContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.editInputHeader}>
              <Text style={styles.editInputLabel}>
                Add New {newElementModal.type === 'readModel' ? 'Read Model' : newElementModal.type}
              </Text>
            </div>
            <input
              type="text"
              placeholder={`Enter ${newElementModal.type} name...`}
              style={styles.editInput}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  addElement(newElementModal.type, newElementModal.sliceIdx, e.target.value);
                } else if (e.key === 'Escape') {
                  setNewElementModal(null);
                }
              }}
            />
            <div style={styles.editInputHint}>
              Press Enter to add, Esc to cancel
            </div>
          </div>
        </div>
      )}

      {/* Edit mode help tooltip */}
      {editMode && !editingElement && !contextMenu && !newElementModal && (
        <div style={styles.editModeHelp}>
          <Text style={styles.editModeHelpText}>
            ✏️ <strong>Edit Mode</strong> - Double-click to rename • Right-click for options
          </Text>
        </div>
      )}
    </div>
  );
}

const truncate = (str, len) => {
  if (!str) return '';
  return str.length > len ? str.substring(0, len - 1) + '…' : str;
};

const styles = {
  container: {
    backgroundColor: 'var(--color-surface, white)',
    borderRadius: tokens.borderRadius.lg,
    border: '1px solid var(--color-border, #E8E4DD)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacing[4],
    borderBottom: '1px solid var(--color-border, #E8E4DD)',
  },
  title: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: 'var(--color-text, #111827)',
  },
  subtitle: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted, #7A6F5C)',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
  },
  zoomBtn: {
    width: '28px',
    height: '28px',
    border: '1px solid var(--color-border, #D4CFC5)',
    borderRadius: tokens.borderRadius.md,
    backgroundColor: 'var(--color-surface, white)',
    cursor: 'pointer',
    fontSize: '16px',
  },
  zoomLevel: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted, #7A6F5C)',
    minWidth: '40px',
    textAlign: 'center',
  },
  legend: {
    display: 'flex',
    gap: tokens.spacing[4],
    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
    backgroundColor: 'var(--color-surface-secondary, #f9fafb)',
    borderBottom: '1px solid var(--color-border, #E8E4DD)',
    alignItems: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
  },
  legendBox: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
  },
  legendText: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted, #7A6F5C)',
  },
  legendHint: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted, #9ca3af)',
    marginLeft: 'auto',
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'row',
    maxHeight: '700px',
    overflow: 'hidden',
  },
  diagramWrapper: {
    flex: 1,
    overflow: 'auto',
    padding: tokens.spacing[3],
    backgroundColor: '#FFF8EE',
    minHeight: '500px',
    maxHeight: '700px',
    // Custom scrollbar styling
    scrollbarWidth: 'thin',
    scrollbarColor: '#D4CFC5 #f9fafb',
  },
  detailPanel: {
    width: '400px',
    flexShrink: 0,
    borderLeft: '1px solid var(--color-border, #E8E4DD)',
    backgroundColor: 'var(--color-surface, white)',
    padding: tokens.spacing[4],
    overflowY: 'auto',
    maxHeight: '700px',
  },
  detailHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: tokens.spacing[3],
  },
  sliceBadge: {
    fontSize: '10px',
    fontWeight: '700',
    color: COLORS.command.bg,
    letterSpacing: '0.05em',
    marginBottom: '4px',
  },
  detailTitle: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.bold,
    color: 'var(--color-text, #111827)',
    fontFamily: tokens.typography.fontFamily.mono,
  },
  detailChapter: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted, #7A6F5C)',
    marginTop: '4px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: 'var(--color-text-muted, #9ca3af)',
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  },
  typeBadge: {
    marginBottom: tokens.spacing[4],
  },
  badge: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'white',
    padding: '4px 10px',
    borderRadius: tokens.borderRadius.sm,
    textTransform: 'uppercase',
  },
  detailSection: {
    marginBottom: tokens.spacing[4],
  },
  sectionTitle: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: 'var(--color-text)',
    marginBottom: tokens.spacing[2],
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacing[2],
  },
  tag: {
    fontSize: '12px',
    color: 'white',
    padding: '6px 12px',
    borderRadius: tokens.borderRadius.md,
    fontFamily: tokens.typography.fontFamily.mono,
    fontWeight: '500',
  },
  gwtList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[3],
  },
  gwtCard: {
    backgroundColor: 'var(--color-surface-secondary, #f9fafb)',
    borderRadius: tokens.borderRadius.md,
    padding: tokens.spacing[3],
    border: '1px solid var(--color-border, #E8E4DD)',
  },
  gwtRow: {
    marginBottom: tokens.spacing[2],
  },
  gwtLabel: {
    display: 'inline-block',
    fontSize: '10px',
    fontWeight: '700',
    color: '#7B6BA8',
    letterSpacing: '0.05em',
    marginRight: tokens.spacing[2],
    minWidth: '50px',
  },
  gwtText: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    lineHeight: 1.6,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing[8],
    backgroundColor: 'var(--color-surface, white)',
    borderRadius: tokens.borderRadius.lg,
    border: '1px solid var(--color-border, #E8E4DD)',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: tokens.spacing[3],
  },
  emptyTitle: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: 'var(--color-text, #111827)',
    marginBottom: tokens.spacing[2],
  },
  emptyText: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text-muted, #7A6F5C)',
  },
  // Screen detail panel styles
  screenCompCard: {
    backgroundColor: 'var(--color-surface-secondary, #f9fafb)',
    borderRadius: tokens.borderRadius.md,
    padding: tokens.spacing[2],
    border: '1px solid var(--color-border, #E8E4DD)',
  },
  compRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    marginBottom: tokens.spacing[1],
  },
  compIcon: {
    fontSize: '14px',
  },
  compLabel: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: 'var(--color-text)',
  },
  compTarget: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted, #7A6F5C)',
    marginLeft: tokens.spacing[5],
  },
  transitionCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    backgroundColor: 'var(--color-surface-secondary, #f9fafb)',
    borderRadius: tokens.borderRadius.md,
    padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
    border: '1px solid var(--color-border, #E8E4DD)',
  },
  transitionTrigger: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted, #7A6F5C)',
  },
  transitionArrow: {
    fontSize: '14px',
    color: COLORS.screen.border,
  },
  transitionTarget: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.medium,
    color: COLORS.screen.text,
  },
  // Validation styles
  validationBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    border: '1px solid #D4CFC5',
    borderRadius: tokens.borderRadius.md,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  issueBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '18px',
    height: '18px',
    padding: '0 5px',
    borderRadius: '9px',
    backgroundColor: '#D24E26',
    color: 'white',
    fontSize: '10px',
    fontWeight: '700',
  },
  controlDivider: {
    width: '1px',
    height: '20px',
    backgroundColor: '#E8E4DD',
    margin: '0 8px',
  },
  validationPanel: {
    width: '360px',
    flexShrink: 0,
    borderLeft: '1px solid #E8A48E',
    backgroundColor: '#FDF0EB',
    padding: tokens.spacing[4],
    overflowY: 'auto',
    maxHeight: '700px',
  },
  validationSectionTitle: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: '#8B3215',
    marginBottom: tokens.spacing[1],
  },
  validationHint: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: '#A35A1E',
    marginBottom: tokens.spacing[2],
  },
  validationList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[2],
  },
  validationItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    padding: tokens.spacing[2],
    backgroundColor: '#FFF8EE',
    borderRadius: tokens.borderRadius.md,
    border: `1px solid ${tokens.colors.error[100]}`,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  validationChapter: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: tokens.colors.neutral[500],
  },
  noIssuesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing[6],
    textAlign: 'center',
  },
  noIssuesIcon: {
    fontSize: '48px',
    marginBottom: tokens.spacing[3],
  },
  noIssuesTitle: {
    fontSize: tokens.typography.fontSize.lg[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: tokens.colors.success[700],
    marginBottom: tokens.spacing[2],
  },
  noIssuesText: {
    fontSize: tokens.typography.fontSize.sm[0],
    color: tokens.colors.neutral[500],
    lineHeight: 1.5,
  },
  // Edit mode styles
  editModeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    border: '1px solid #D4CFC5',
    borderRadius: tokens.borderRadius.md,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  editOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  editInputContainer: {
    backgroundColor: '#FFF8EE',
    borderRadius: tokens.borderRadius.lg,
    padding: tokens.spacing[4],
    boxShadow: '0 10px 25px rgba(13, 9, 3, 0.2)',
    minWidth: '320px',
  },
  editInputHeader: {
    marginBottom: tokens.spacing[2],
  },
  editInputLabel: {
    fontSize: tokens.typography.fontSize.sm[0],
    fontWeight: tokens.typography.fontWeight.semibold,
    color: 'var(--color-text)',
    textTransform: 'capitalize',
  },
  editInput: {
    width: '100%',
    padding: tokens.spacing[3],
    fontSize: tokens.typography.fontSize.base[0],
    fontFamily: tokens.typography.fontFamily.mono,
    border: '2px solid #5B8FD9',
    borderRadius: tokens.borderRadius.md,
    outline: 'none',
    boxSizing: 'border-box',
  },
  editInputHint: {
    marginTop: tokens.spacing[2],
    fontSize: tokens.typography.fontSize.xs[0],
    color: 'var(--color-text-muted, #7A6F5C)',
  },
  contextMenuOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000,
  },
  contextMenu: {
    position: 'fixed',
    backgroundColor: '#FFF8EE',
    borderRadius: tokens.borderRadius.md,
    boxShadow: '0 4px 12px rgba(13, 9, 3, 0.15)',
    border: '1px solid var(--color-border)',
    minWidth: '140px',
    overflow: 'hidden',
  },
  contextMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacing[2],
    width: '100%',
    padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
    fontSize: tokens.typography.fontSize.sm[0],
    color: 'var(--color-text)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s',
  },
  editModeHelp: {
    position: 'absolute',
    bottom: tokens.spacing[4],
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: tokens.colors.primary[400],
    color: tokens.colors.neutral[50],
    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
    borderRadius: tokens.borderRadius.full,
    boxShadow: '0 4px 12px rgba(13, 9, 3, 0.2)',
    zIndex: 100,
  },
  editModeHelpText: {
    fontSize: tokens.typography.fontSize.xs[0],
    color: tokens.colors.neutral[50],
  },
};

export default SystemFlowDiagram;
